export class AuthApiClient {
    authUrl;
    refreshTimer = null;
    /**
     * In-memory expiry timestamp (ms since epoch). NOT persisted to localStorage.
     * JS never holds the actual token — this is only used to schedule proactive
     * refresh based on `expires_in` returned by login / refresh responses.
     */
    expiresAt = null;
    constructor(authUrl) {
        this.authUrl = authUrl;
    }
    async request(method, path, body) {
        const headers = {
            "Content-Type": "application/json",
        };
        // No Authorization header — the HttpOnly cookie is sent automatically
        // by the browser because credentials: "include" is set.
        const res = await fetch(`${this.authUrl}${path}`, {
            method,
            headers,
            credentials: "include",
            body: body ? JSON.stringify(body) : undefined,
        });
        if (!res.ok) {
            const errorBody = await res
                .json()
                .catch(() => ({ message: res.statusText }));
            throw new Error(errorBody.message ?? res.statusText);
        }
        return res.json();
    }
    // --- Login flow ---
    async loginPassword(email, password) {
        return this.request("POST", "/login", { email, password });
    }
    async verifyMFA(code, sessionToken, email) {
        return this.request("POST", "/login/mfa", {
            code,
            session: sessionToken,
            email,
        });
    }
    async setNewPassword(session, email, newPassword, name) {
        return this.request("POST", "/login/new-password", {
            session,
            email,
            new_password: newPassword,
            name,
        });
    }
    // --- Password reset ---
    async forgotPassword(email) {
        await this.request("POST", "/forgot-password", { email });
    }
    async resetPassword(email, code, newPassword) {
        await this.request("POST", "/reset-password", {
            email,
            code,
            new_password: newPassword,
        });
    }
    // --- Credential management (authenticated) ---
    async changeEmail(email) {
        await this.request("POST", "/users/me/email", { email });
    }
    async verifyEmailChange(code) {
        await this.request("POST", "/users/me/email/verify", { code });
    }
    async changePassword(currentPassword, newPassword) {
        await this.request("POST", "/users/me/password", {
            current_password: currentPassword,
            new_password: newPassword,
        });
    }
    async removeMFA(password, mfaCode) {
        await this.request("POST", "/users/me/mfa/remove", {
            password,
            mfa_code: mfaCode,
        });
    }
    // --- MFA setup ---
    async initiateMFASetup() {
        return this.request("GET", "/mfa/setup");
    }
    async verifyMFASetup(code) {
        return this.request("POST", "/mfa/setup/verify", { code });
    }
    // --- Token refresh ---
    /**
     * Attempt a silent token refresh. No request body — the HttpOnly refresh
     * cookie is sent automatically. Returns the server's `expires_in` value.
     * Throws on failure (network error or non-2xx response).
     */
    async refreshToken() {
        // POST /refresh with no body — the __Secure-duvi-rt cookie is sent
        // automatically via credentials: "include".
        const result = await this.request("POST", "/refresh");
        if (result.data?.expires_in) {
            this.recordExpiry(result.data.expires_in);
        }
        return result;
    }
    /**
     * Bootstrap auth state on app init. Probes /refresh — success means the
     * HttpOnly refresh cookie is still valid. Returns expires_in on success,
     * throws on failure (cookie expired / not present).
     */
    async bootstrap() {
        const result = await this.refreshToken();
        return result.data.expires_in;
    }
    /**
     * Record in-memory expiry from an `expires_in` (seconds) value.
     * Called after a successful login or refresh.
     */
    recordExpiry(expiresIn) {
        this.expiresAt = Date.now() + expiresIn * 1000;
    }
    scheduleRefresh(onFailed) {
        this.cancelRefresh();
        if (!this.expiresAt)
            return;
        const bufferMs = 5 * 60 * 1000;
        const delayMs = Math.max(0, this.expiresAt - Date.now() - bufferMs);
        console.debug(`[auth] scheduling token refresh in ${Math.round(delayMs / 1000)}s`);
        this.refreshTimer = setTimeout(async () => {
            try {
                await this.refreshToken();
                console.debug("[auth] proactive refresh succeeded");
                this.scheduleRefresh(onFailed);
            }
            catch (err) {
                console.error("[auth] proactive refresh failed", err);
                if (onFailed)
                    onFailed();
            }
        }, delayMs);
    }
    cancelRefresh() {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
    }
    // --- Logout ---
    async logout() {
        try {
            await this.request("POST", "/logout");
        }
        catch {
            // Best-effort — clear in-memory state even if the API call fails.
        }
        this.expiresAt = null;
    }
}
//# sourceMappingURL=auth-client.js.map